import { WallConfig, WallPropConfig } from "@/state/RoomConfig/WallConfig";
import React, { useEffect, useMemo, useRef } from "react";
import {
    getWallPosition,
    getWallRotation,
    getWallSizePropForWallKey,
    isWallKey,
    RoomConfig,
} from "@/state/RoomConfig/RoomConfig";
import { MaterialTextures } from "@/state/MaterialResources";

import { Base, CSGGeometryRef, Geometry, Subtraction } from "@react-three/csg";
import { EdgesGeometry, PlaneGeometry, Vector3Like } from "three";
import { useWallPropData } from "./useWallPropData";
import { DoorPropView } from "./DoorPropView";
import { WindowPropView } from "./WindowPropView";
import { useDemoState } from "@/state/demoState";
import { createSelectedWall, SELECTED_ITEM_TYPE } from "@/3d/SelectedItem";
import { THEME_COLORS } from "@/utils/colors";
import { Baseboard } from "@/3d/proceduralMesh/Baseboard";

type Props = {
    wall: WallConfig;
    room: RoomConfig;
    textures: MaterialTextures;
};

export const Wall: React.FC<Props> = ({ wall, room, textures }) => {
    const selectedItem = useDemoState((state) => state.selectedItem);
    const isWallSelected =
        selectedItem !== undefined &&
        selectedItem.selectedItemType === SELECTED_ITEM_TYPE.WALL &&
        selectedItem.key === wall.key;

    const width = useMemo(
        () => (isWallKey(wall.key) ? room[getWallSizePropForWallKey(wall.key)] : 0),
        [wall.key, room]
    );

    const height = useMemo(() => room.height, [room]);

    const position = useMemo(
        () => getWallPosition(wall, room, { includeHeight: true }),
        [wall, room]
    );

    const rotation = useMemo(() => getWallRotation(wall, room), [wall, room]);

    const { geometry, edges } = useMemo(() => {
        const geometry = new PlaneGeometry(width, height);

        // resize uvs
        const uvs = geometry.attributes.uv;
        for (let i = 0; i < uvs.count; i++) {
            uvs.array[i * 2] = uvs.array[i * 2] * width * 0.5;
            uvs.array[i * 2 + 1] = uvs.array[i * 2 + 1] * height * 0.5;
        }

        const edges = new EdgesGeometry(geometry);

        return { geometry, edges };
    }, [width, height]);

    const csgGeometryRef = useRef<CSGGeometryRef>(null);

    useEffect(() => {
        csgGeometryRef.current?.update();
    }, [wall.props]);

    return (
        <group position={position} rotation={[0, rotation, 0]}>
            <lineSegments visible={isWallSelected} args={[edges]} renderOrder={999}>
                <lineBasicMaterial
                    color={THEME_COLORS.highlightedItemBlue}
                    depthTest={false}
                    linewidth={100}
                />
            </lineSegments>

            <mesh receiveShadow userData={createSelectedWall(wall)}>
                <meshStandardMaterial
                    roughnessMap={textures.roughness}
                    normalMap={textures.normal}
                    bumpMap={textures.displacement}
                    color={wall.color}
                />
                <Geometry ref={csgGeometryRef}>
                    <Base geometry={geometry} />
                    {wall.props.map((prop) => (
                        <WallPropSubstraction
                            key={prop.key}
                            prop={prop}
                            delta={{ x: -width / 2, y: -room.height / 2, z: 0 }}
                        />
                    ))}
                </Geometry>
            </mesh>
            <group position={[-width / 2, -room.height / 2, 0]}>
                <Baseboard
                    width={0.1}
                    height={0.1}
                    length={width}
                    color={wall.baseboardColor}
                    wallProps={wall.props}
                />
                {wall.props.map((prop) =>
                    prop.type === "door" ? (
                        <DoorPropView key={prop.key} prop={prop} wall={wall} />
                    ) : (
                        <WindowPropView key={prop.key} prop={prop} wall={wall} />
                    )
                )}
            </group>
        </group>
    );
};

type WallPropSubstractionProps = {
    prop: WallPropConfig;
    delta: Vector3Like;
};

export const WallPropSubstraction: React.FC<WallPropSubstractionProps> = ({ prop, delta }) => {
    const { position, floorOffset } = useWallPropData(prop);

    return (
        <Subtraction
            position={[position + delta.x, floorOffset + prop.height / 2 + delta.y, delta.z]}
            scale={[prop.width, prop.height, 1]}
        >
            <boxGeometry />
        </Subtraction>
    );
};
